import { keyBetween, newUuidv7 } from '@pf/sync-rules';
import { useParams } from '@tanstack/react-router';
import { History, Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../api.ts';
import f from '../app/Dialog.module.css';
import { Dialog } from '../app/Dialog.tsx';
import { type Me, useMe, useTrip } from '../app/data.ts';
import s from '../app/Notes.module.css';
import { Markdown, NoteEditor, Toggle } from '../app/Notes.tsx';
import { stampNow } from '../clock.ts';
import { cx } from '../kit/cx.ts';
import { Button } from '../kit/index.ts';
import t from '../kit/type.module.css';
import type { Checklist, TripModel } from '../model/trip.ts';
import type { Edit } from '../sync.ts';

type Note = TripModel['notes'][number];

/** Checklist templates, as the apps' ChecklistTemplates (FR-NOTE-04). */
const TEMPLATES: [string, string[]][] = [
  [
    'Packing',
    [
      'Passport',
      'Phone charger and cable',
      'Power adapter (type A)',
      'Medicines',
      'Toiletries',
      'Clothes for 4 days',
      'Comfortable walking shoes',
      'Light jacket',
      'Sunglasses',
      'Reusable bag',
      'Small towel',
      'Coin pouch',
    ],
  ],
  [
    'Before you fly',
    [
      'Online check-in',
      'Seats chosen',
      'Bags weighed',
      'eSIM or pocket Wi-Fi ready',
      'Visit Japan Web QR codes',
      'Cash for the first day',
      'Offline maps downloaded',
      'Pre-trip PDF saved',
      'Hotel addresses in Japanese',
      'Travel insurance documents',
      'Tell the bank about the trip',
    ],
  ],
  [
    'Japan essentials',
    [
      'Suica / Pasmo in Wallet',
      'IC card topped up',
      'Rail passes activated',
      'Coin purse',
      'Trash bag',
      'Hand towel',
      'Cash for small shops',
      'Translation app offline pack',
    ],
  ],
];

const removed = () => ({ deletedAt: stampNow() });

/** F-03 Info: the trip's notes (Markdown, as the apps store them) and checklists. */
export function Info() {
  const { tripId } = useParams({ from: '/app/trips/$tripId/info' });
  const { model, save, error } = useTrip(tripId);
  const me = useMe();
  const [creating, setCreating] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  if (error)
    return (
      <p className={cx(s.pad, t.bodyS)} role="alert">
        {error.message}
      </p>
    );
  if (!model || !me.data) return <p className={cx(s.pad, t.bodyS)}>Loading…</p>;
  const run = async (edits: Edit[]) => {
    const refused = await save(edits);
    setNotice(refused > 0 ? "A change wasn't saved; the screen shows what the server has." : null);
  };
  const notes = [...model.notes].sort(
    (a, b) => a.sortKey.localeCompare(b.sortKey) || a.id.localeCompare(b.id),
  );
  const lastNoteKey = notes.at(-1)?.sortKey ?? null;
  const lastListKey = model.checklists.at(-1)?.sortKey ?? null;

  return (
    <div className={s.page}>
      <header className={s.top}>
        <div className={cx(t.readoutS, s.muted)}>{`// ${model.title}`}</div>
        <h1 className={cx(t.displayL, s.flat)}>Info</h1>
      </header>
      {notice && (
        <p className={cx(s.notice, t.bodyS)} role="status">
          {notice}
        </p>
      )}
      <div className={s.grid}>
        <section aria-labelledby="pf-notes">
          <div className={s.head}>
            <h2 id="pf-notes" className={cx(t.label, s.flat)}>
              Notes
            </h2>
            <Button variant="ghost" size="small" icon={Plus} onClick={() => setCreating(true)}>
              Note
            </Button>
          </div>
          {creating && (
            <NoteEditor
              model={model}
              me={me.data}
              onCancel={() => setCreating(false)}
              onSave={async (title, body, personal) => {
                await run([
                  {
                    table: 'note',
                    id: newUuidv7(),
                    tripId,
                    ownerUserId: personal ? me.data.user.id : null,
                    fields: {
                      targetType: 'trip',
                      targetId: null,
                      title,
                      body,
                      sortKey: keyBetween(lastNoteKey, null),
                    },
                  },
                ]);
                setCreating(false);
              }}
            />
          )}
          {notes.length === 0 && !creating && <p className={cx(s.pad, t.bodyS, s.muted)}>No notes yet.</p>}
          {notes.map((n) => (
            <NoteCard key={n.id} note={n} model={model} me={me.data} run={run} />
          ))}
        </section>
        <section aria-labelledby="pf-lists">
          <div className={s.head}>
            <h2 id="pf-lists" className={cx(t.label, s.flat)}>
              Checklists
            </h2>
            <NewChecklist
              me={me.data.user.id}
              onAdd={(edits) => void run(edits)}
              tripId={tripId}
              after={lastListKey}
            />
          </div>
          {model.checklists.length === 0 && <p className={cx(s.pad, t.bodyS, s.muted)}>No checklists yet.</p>}
          {model.checklists.map((c) => (
            <ChecklistCard key={c.id} list={c} tripId={tripId} run={run} />
          ))}
        </section>
      </div>
    </div>
  );
}

// ── Notes ────────────────────────────────────────────────────────────────────────

function target(model: TripModel, n: Note): string | null {
  if (n.targetType === 'day') {
    const d = model.days.find((x) => x.id === n.targetId);
    return d ? `Day ${String(d.number).padStart(2, '0')}` : 'A day';
  }
  if (n.targetType === 'destination')
    return model.destinations.find((x) => x.id === n.targetId)?.name ?? 'A destination';
  if (n.targetType === 'item') {
    for (const d of model.days)
      for (const p of d.items) if (p.item.id === n.targetId) return `${p.stopId} ${p.item.title}`;
    return 'A place';
  }
  return null;
}

function NoteCard({
  note,
  model,
  me,
  run,
}: {
  note: Note;
  model: TripModel;
  me: Me;
  run: (e: Edit[]) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [history, setHistory] = useState(false);
  const write = (fields: Record<string, string>) =>
    run([{ table: 'note', id: note.id, tripId: model.id, ownerUserId: note.ownerUserId, fields }]);
  const about = target(model, note);
  if (editing)
    return (
      <NoteEditor
        model={model}
        me={me}
        note={note}
        onCancel={() => setEditing(false)}
        onDelete={async () => {
          await run([
            {
              table: 'note',
              id: note.id,
              tripId: model.id,
              ownerUserId: note.ownerUserId,
              fields: removed(),
            },
          ]);
          setEditing(false);
        }}
        onSave={async (title, body) => {
          await write({ title, body });
          setEditing(false);
        }}
      />
    );
  return (
    <article className={s.card}>
      <div className={s.cardHead}>
        <h3 className={cx(t.titleL, s.flat)}>{note.title || 'Untitled note'}</h3>
        {note.ownerUserId && <span className={cx(t.label, s.mine)}>Mine</span>}
        <span className={s.spacer} />
        <button
          type="button"
          className={s.icon}
          aria-label="Earlier versions"
          onClick={() => setHistory(true)}
        >
          <History size={16} />
        </button>
        <button type="button" className={s.icon} aria-label="Edit note" onClick={() => setEditing(true)}>
          <Pencil size={16} />
        </button>
      </div>
      {about && <div className={cx(t.caption, s.muted)}>{about}</div>}
      <Markdown text={note.body} model={model} onToggle={(body) => void write({ body })} />
      {history && (
        <NoteHistory
          note={note}
          me={me}
          onClose={() => setHistory(false)}
          onRestore={async (v) => {
            await write(v);
            setHistory(false);
          }}
        />
      )}
    </article>
  );
}

function NoteHistory({
  note,
  me,
  onClose,
  onRestore,
}: {
  note: Note;
  me: Me;
  onClose: () => void;
  onRestore: (v: { title: string; body: string }) => Promise<void>;
}) {
  const [versions, setVersions] = useState<
    { title: string; body: string; updatedBy: string | null; at: string }[] | null
  >(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    api<{ versions: { title: string; body: string; updatedBy: string | null; at: string }[] }>(
      `/notes/${note.id}/versions`,
    ).then(
      (r) => setVersions(r.versions),
      () => setFailed(true),
    );
  }, [note.id]);
  const who = (id: string | null) =>
    id === me.user.id ? 'Me' : id === me.partner?.id ? me.partner.displayName || 'Partner' : '—';
  return (
    <Dialog title="Earlier versions" onClose={onClose} onSubmit={onClose}>
      {failed && <p className={t.bodyS}>Couldn't load the history.</p>}
      {versions?.length === 0 && <p className={t.bodyS}>No earlier versions.</p>}
      <ol className={s.versions}>
        {versions?.map((v) => (
          <li key={v.at}>
            <div className={s.cardHead}>
              <span className={t.readoutS}>{new Date(v.at).toLocaleString()}</span>
              <span className={cx(t.caption, s.muted)}>{who(v.updatedBy)}</span>
              <span className={s.spacer} />
              <Button
                variant="ghost"
                size="small"
                onClick={() => void onRestore({ title: v.title, body: v.body })}
              >
                Restore
              </Button>
            </div>
            <p className={cx(t.caption, s.muted, s.preview)}>{v.body.slice(0, 160)}</p>
          </li>
        ))}
      </ol>
      <div className={f.actions}>
        <span className={f.spacer} />
        <Button variant="primary" type="submit">
          Done
        </Button>
      </div>
    </Dialog>
  );
}

// ── Checklists ───────────────────────────────────────────────────────────────────

function NewChecklist({
  tripId,
  me,
  after,
  onAdd,
}: {
  tripId: string;
  me: string;
  after: string | null;
  onAdd: (edits: Edit[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [template, setTemplate] = useState('');
  const [personal, setPersonal] = useState(false);
  const create = () => {
    const id = newUuidv7();
    const owner = personal ? me : null;
    const chosen = TEMPLATES.find(([name]) => name === template);
    const edits: Edit[] = [
      {
        table: 'checklist',
        id,
        tripId,
        ownerUserId: owner,
        fields: { title: title.trim() || chosen?.[0] || 'Checklist', sortKey: keyBetween(after, null) },
      },
    ];
    let key: string | null = null;
    for (const text of chosen?.[1] ?? []) {
      key = keyBetween(key, null);
      edits.push({
        table: 'checklistEntry',
        id: newUuidv7(),
        tripId,
        ownerUserId: owner,
        fields: { checklistId: id, text, checked: false, sortKey: key },
      });
    }
    onAdd(edits);
    setOpen(false);
    setTitle('');
    setTemplate('');
  };
  return (
    <>
      <Button variant="ghost" size="small" icon={Plus} onClick={() => setOpen(true)}>
        Checklist
      </Button>
      {open && (
        <Dialog title="New checklist" onClose={() => setOpen(false)} onSubmit={create}>
          <input
            aria-label="Checklist title"
            placeholder="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className={f.input}
          />
          <select
            aria-label="From a template"
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
            className={f.input}
          >
            <option value="">Empty</option>
            {TEMPLATES.map(([name, entries]) => (
              <option key={name} value={name}>{`From template: ${name} (${entries.length})`}</option>
            ))}
          </select>
          <label className={cx(f.check, t.bodyS)}>
            <input type="checkbox" checked={personal} onChange={(e) => setPersonal(e.target.checked)} /> Only
            me
          </label>
          <div className={f.actions}>
            <span className={f.spacer} />
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Create
            </Button>
          </div>
        </Dialog>
      )}
    </>
  );
}

function ChecklistCard({
  list,
  tripId,
  run,
}: {
  list: Checklist;
  tripId: string;
  run: (e: Edit[]) => Promise<void>;
}) {
  const [text, setText] = useState('');
  const owner = list.ownerUserId;
  const entry = (id: string, fields: Record<string, string | boolean | null>): Edit => ({
    table: 'checklistEntry',
    id,
    tripId,
    ownerUserId: owner,
    fields,
  });
  const done = list.entries.filter((e) => e.checked).length;
  return (
    <article className={s.card}>
      <div className={s.cardHead}>
        <h3 className={cx(t.label, s.flat)}>{`// ${list.title}`}</h3>
        {owner && <span className={cx(t.label, s.mine)}>Mine</span>}
        <span className={s.spacer} />
        <span className={cx(t.readoutS, s.muted)}>
          {done}/{list.entries.length}
        </span>
        <button
          type="button"
          className={s.icon}
          aria-label={`Delete ${list.title}`}
          onClick={() => {
            if (confirm(`Delete “${list.title}” and its ${list.entries.length} entries?`))
              void run([
                { table: 'checklist', id: list.id, tripId, ownerUserId: owner, fields: removed() },
                ...list.entries.map((e) => entry(e.id, removed())),
              ]);
          }}
        >
          <Trash2 size={16} />
        </button>
      </div>
      <ul className={s.entries}>
        {list.entries.map((e) => (
          <li key={e.id} className={cx(e.checked && s.done)}>
            <Toggle
              className={s.check}
              checked={e.checked}
              onToggle={() => void run([entry(e.id, { checked: !e.checked })])}
            >
              <span className={t.bodyS}>{e.text}</span>
            </Toggle>
            <button
              type="button"
              className={s.icon}
              aria-label={`Remove ${e.text}`}
              onClick={() => void run([entry(e.id, removed())])}
            >
              <Trash2 size={14} />
            </button>
          </li>
        ))}
      </ul>
      <form
        className={s.addEntry}
        onSubmit={(e) => {
          e.preventDefault();
          if (!text.trim()) return;
          void run([
            entry(newUuidv7(), {
              checklistId: list.id,
              text: text.trim(),
              checked: false,
              sortKey: keyBetween(list.entries.at(-1)?.sortKey ?? null, null),
            }),
          ]);
          setText('');
        }}
      >
        <input
          aria-label={`Add to ${list.title}`}
          placeholder="Add an entry"
          value={text}
          onChange={(e) => setText(e.target.value)}
          className={f.input}
        />
      </form>
    </article>
  );
}
